import "reflect-metadata";
import { createApp } from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "@/app/ui/router/index";
import store from "@/store";
import { AppComponent } from "./app/infrastructures/dependencies/AppComponent";
import "@/app/ui/assets/css/tailwind.css";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import "@/app/ui/assets/css/saga-blue.css"; //theme
import "@/app/ui/assets/css/primevue.min.css"; //core css
import "primeicons/primeicons.css"; //icons
import "@splidejs/splide/dist/css/themes/splide-default.min.css";
import "@/app/ui/assets/css/style.css";
import formatDate from "@/app/infrastructures/misc/common-library/FormatDate";
import formatDateWithoutTime from "@/app/infrastructures/misc/common-library/FormatDateWithoutTime";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/FormatPhoneNumber";
import { getPrimeVueLocaleConfig } from "@/app/infrastructures/misc/common-library/PrimeVueLocaleConfig";
import Tooltip from "primevue/tooltip";
import VueGtag from "vue-gtag";
import moment from "moment";
import MedusaComponents from "@/app/ui/components";
import MedusaDirectives from "@/directives";
import { gtmModule } from "./gtm";
import "vue-client-recaptcha/dist/style.css";
import i18n from "@/i18n";
import * as Sentry from "@sentry/vue";
import DOMPurify from "dompurify";
moment.locale("id");
import "@/app/ui/assets/fonts";
import { initRollout } from "@/feature-flags";
import { initUnleashFeatureFlags } from "@/feature-flags/unleash-exports";
import Hotjar from "vue-hotjar";
import posthog from "posthog-js";
import { SetViewportScale } from "@/app/infrastructures/misc/common-library/SetViewportScale";
import { mountRecaptchaV3 } from "./app/infrastructures/misc/common-library/RecaptchaV3";
import { setupAccountWatcher } from "./feature-flags/unleash-context-sync";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { syncLocaleFromHost } from "./app/infrastructures/misc/syncLocaleFromHost";

const globalPosthog = posthog.init(process.env.VUE_APP_POSTHOG_KEY || "", {
  // eslint-disable-next-line @typescript-eslint/camelcase
  api_host: process.env.VUE_APP_POSTHOG_HOST,
  // eslint-disable-next-line @typescript-eslint/camelcase
  person_profiles: "always",
  // eslint-disable-next-line @typescript-eslint/camelcase
  disable_session_recording: true
});

export const app = createApp(App);
AppComponent.init();

app.directive("sanitize-html", (el, binding) => {
  el.innerHTML = DOMPurify.sanitize(binding.value, { ADD_ATTR: ["target"] });
});

MedusaComponents.register(app);
MedusaDirectives.register(app);
if (process.env.VUE_APP_SENTRY_DSN && process.env.VUE_APP_SENTRY_TARGET) {
  Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration()
    ],
    tracesSampleRate:
      process.env.VUE_APP_ENVIRONMENTS === "production" ? 0.001 : 0.1,
    environment: process.env.VUE_APP_ENVIRONMENTS,
    sampleRate: process.env.VUE_APP_ENVIRONMENTS === "production" ? 0.5 : 0,
    beforeSend(event) {
      // Ignore ChunkLoadError
      if (event.exception) {
        const errorType = event.exception.values?.[0]?.type;
        if (errorType === "ChunkLoadError") {
          return null;
        }
      }
      return event;
    }
  });
}
app
  .use({
    install: (el) => {
      el.config.globalProperties.$moment = moment;
      el.config.globalProperties.$posthog = globalPosthog;
    }
  })
  .mixin({
    methods: {
      formatDate,
      formatDateWithoutTime,
      formatPhoneNumber
    }
  })
  .directive("tooltip", Tooltip)
  .use(PrimeVue, {
    locale: {
      ...getPrimeVueLocaleConfig(
        AccountController.accountData?.account_type_detail?.countryCode ||
          (typeof sessionStorage !== "undefined"
            ? sessionStorage.getItem("countryCode") || "id"
            : "id")
      )
    }
  })
  .use(i18n)
  .use(ToastService)
  .use(Hotjar, {
    id: process.env.VUE_APP_HOTJAR_ID,
    isProduction:
      process.env.VUE_APP_ENVIRONMENTS === "production" ||
      process.env.VUE_APP_ENVIRONMENTS === "staging"
  });

export async function mount(selector = "#app") {
  syncLocaleFromHost();
  mountRecaptchaV3();
  await initRollout();
  // Initialize Unleash feature flags
  await initUnleashFeatureFlags(app);
  SetViewportScale();
  app
    .use(store)
    .use(router)
    .use(
      VueGtag,
      {
        config: { id: process.env.VUE_APP_GOOGLE_ANALYTCS }
      },
      router,
      gtmModule
    )
    .mount(selector);
  setupAccountWatcher();
}

export function unmount() {
  if (app) {
    app.unmount();
  }
}

// Only auto-mount if running standalone
if (process.env.VUE_APP_MODULE_FEDERATION !== "true") {
  mount();
}